/** How many levels of JSON-inside-a-string are unpacked before we stop digging. */
const MAX_EXPAND_DEPTH = 10;

/**
 * Integration payloads regularly carry a second JSON document inside a string property.
 * A string is treated as embedded JSON when it is wrapped in {} or [] - everything else
 * is left as the plain string it is.
 */
export const looksLikeJson = (value: string): boolean => {
  const trimmed = value.trim();

  return (trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'));
};

const expandEmbeddedJson = (value: unknown, depth: number): unknown => {
  if (depth >= MAX_EXPAND_DEPTH) return value;

  if (typeof value === 'string') {
    if (!looksLikeJson(value)) return value;

    try {
      return expandEmbeddedJson(JSON.parse(value), depth + 1);
    } catch {
      return value;
    }
  }

  if (Array.isArray(value)) return value.map((item) => expandEmbeddedJson(item, depth + 1));

  if (value !== null && typeof value === 'object') {
    const expanded: Record<string, unknown> = {};

    Object.keys(value).forEach((key) => {
      expanded[key] = expandEmbeddedJson((value as Record<string, unknown>)[key], depth + 1);
    });

    return expanded;
  }

  return value;
};

/**
 * Pretty prints a JSON string and unpacks the JSON documents that are stored as strings
 * inside it, so they read as objects instead of one long escaped line. Anything that is
 * not JSON is handed back untouched.
 */
export const formatJson = (raw?: string): string => {
  if (!raw) return '';

  try {
    return JSON.stringify(expandEmbeddedJson(JSON.parse(raw), 0), null, 2);
  } catch {
    return raw;
  }
};
